import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../utils/apiConfig';

interface BusinessSetting {
    id: string;
    businessName: string;
    invoicePrefix: string;
}

interface Gym {
    id: string;
    displayName: string;
    email: string;
    address: string | null;
    gstin: string | null;
    includeGstinInInvoices?: boolean;
    phoneNumbers: string[];
}

interface LineItem {
    description: string;
    quantity: string;
    unitPrice: string;
}

interface Invoice {
    id: string;
    businessSettingId: string;
    clientType: string;
    clientGymId?: string;
    clientName: string;
    clientAddress?: string;
    clientPhone?: string;
    clientGstin?: string;
    invoiceDate: string;
    dueDate?: string;
    lineItems: Array<{
        description: string;
        quantity: number;
        unitPrice: number;
    }>;
    discountPercent: number;
    discountAmount: number;
    taxPercent: number;
    notes?: string;
    termsAndConditions?: string;
    status: string;
}

interface Props {
    token: string;
    invoice?: Invoice | null;
    prefilledData?: any;
    onClose: () => void;
    onSuccess: () => void;
}

export default function InvoiceWizard({ token, invoice, prefilledData, onClose, onSuccess }: Props) {
    const [step, setStep] = useState(1);
    const [businessSettings, setBusinessSettings] = useState<BusinessSetting[]>([]);
    const [gyms, setGyms] = useState<Gym[]>([]);
    const [loading, setLoading] = useState(false);

    // Form data
    const [selectedBusiness, setSelectedBusiness] = useState('');
    const [clientType, setClientType] = useState<'gym' | 'custom'>('gym');
    const [selectedGym, setSelectedGym] = useState('');
    const [customClient, setCustomClient] = useState({
        name: '',
        address: '',
        phone: '',
        gstin: '',
    });
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [dueDate, setDueDate] = useState('');
    const [lineItems, setLineItems] = useState<LineItem[]>([
        { description: '', quantity: '1', unitPrice: '0' }
    ]);
    const [discountType, setDiscountType] = useState<'percent' | 'amount'>('percent');
    const [discountValue, setDiscountValue] = useState('0');
    const [taxPercent, setTaxPercent] = useState('18');
    const [notes, setNotes] = useState('');
    const [termsAndConditions, setTermsAndConditions] = useState('');
    const [status, setStatus] = useState('issued');

    useEffect(() => {
        fetchBusinessSettings();
        fetchGyms();

        // Pre-fill if editing
        if (invoice) {
            setSelectedBusiness(invoice.businessSettingId);
            setClientType(invoice.clientType as 'gym' | 'custom');
            if (invoice.clientType === 'gym' && invoice.clientGymId) {
                setSelectedGym(invoice.clientGymId);
            } else {
                setCustomClient({
                    name: invoice.clientName,
                    address: invoice.clientAddress || '',
                    phone: invoice.clientPhone || '',
                    gstin: invoice.clientGstin || '',
                });
            }
            setInvoiceDate(invoice.invoiceDate.split('T')[0]);
            setDueDate(invoice.dueDate ? invoice.dueDate.split('T')[0] : '');
            setLineItems(invoice.lineItems.map(item => ({
                description: item.description,
                quantity: item.quantity.toString(),
                unitPrice: item.unitPrice.toString(),
            })));
            setDiscountType(invoice.discountPercent > 0 ? 'percent' : 'amount');
            setDiscountValue((invoice.discountPercent > 0 ? invoice.discountPercent : invoice.discountAmount).toString());
            setTaxPercent(invoice.taxPercent.toString());
            setNotes(invoice.notes || '');
            setTermsAndConditions(invoice.termsAndConditions || '');
            setStatus(invoice.status);
        } else if (prefilledData) {
            // Pre-fill if providing initial data (for new invoice, e.g. duplicating an existing one)
            if (prefilledData.businessSettingId) setSelectedBusiness(prefilledData.businessSettingId);
            if (prefilledData.clientType) setClientType(prefilledData.clientType);
            if (prefilledData.clientGymId) setSelectedGym(prefilledData.clientGymId);
            if (prefilledData.clientName || prefilledData.clientAddress || prefilledData.clientPhone || prefilledData.clientGstin) {
                setCustomClient({
                    name: prefilledData.clientName || '',
                    address: prefilledData.clientAddress || '',
                    phone: prefilledData.clientPhone || '',
                    gstin: prefilledData.clientGstin || '',
                });
            }
            if (prefilledData.lineItems) {
                setLineItems(prefilledData.lineItems.map((item: any) => ({
                    description: item.description,
                    quantity: item.quantity.toString(),
                    unitPrice: item.unitPrice.toString(),
                })));
            }
            if (prefilledData.discountPercent > 0) {
                setDiscountType('percent');
                setDiscountValue(prefilledData.discountPercent.toString());
            } else if (prefilledData.discountAmount > 0) {
                setDiscountType('amount');
                setDiscountValue(prefilledData.discountAmount.toString());
            }
            if (prefilledData.taxPercent !== undefined) setTaxPercent(prefilledData.taxPercent.toString());
            if (prefilledData.notes) setNotes(prefilledData.notes);
            if (prefilledData.termsAndConditions) setTermsAndConditions(prefilledData.termsAndConditions);
        }
    }, [invoice, prefilledData]);

    const fetchBusinessSettings = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/saas/invoices/business-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to fetch business settings');

            const data = await response.json();
            setBusinessSettings(data.data.filter((s: BusinessSetting & { isActive: boolean }) => s.isActive));
        } catch (error) {
            console.error('Error fetching business settings:', error);
            toast.error('Failed to fetch business settings');
        }
    };

    const fetchGyms = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/saas/invoices/gyms-list`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to fetch gyms');

            const data = await response.json();
            setGyms(data.data);
        } catch (error) {
            console.error('Error fetching gyms:', error);
            toast.error('Failed to fetch gyms');
        }
    };

    const addLineItem = () => {
        setLineItems([...lineItems, { description: '', quantity: '1', unitPrice: '0' }]);
    };

    const removeLineItem = (index: number) => {
        if (lineItems.length > 1) {
            setLineItems(lineItems.filter((_, i) => i !== index));
        }
    };

    const updateLineItem = (index: number, field: keyof LineItem, value: string) => {
        const updated = [...lineItems];
        updated[index][field] = value;
        setLineItems(updated);
    };

    const calculateSubtotal = () => {
        return lineItems.reduce((sum, item) => {
            const qty = parseFloat(item.quantity) || 0;
            const price = parseFloat(item.unitPrice) || 0;
            return sum + (qty * price);
        }, 0);
    };

    const calculateDiscount = () => {
        const subtotal = calculateSubtotal();
        if (discountType === 'percent') {
            return subtotal * (parseFloat(discountValue) || 0) / 100;
        }
        return parseFloat(discountValue) || 0;
    };

    const calculateTax = () => {
        const subtotal = calculateSubtotal();
        const discount = calculateDiscount();
        return (subtotal - discount) * (parseFloat(taxPercent) || 0) / 100;
    };

    const calculateTotal = () => {
        const subtotal = calculateSubtotal();
        const discount = calculateDiscount();
        const tax = calculateTax();
        return subtotal - discount + tax;
    };

    const handleNext = () => {
        if (step === 1) {
            if (!selectedBusiness) {
                toast.error('Please select a business');
                return;
            }
            if (clientType === 'gym' && !selectedGym) {
                toast.error('Please select a gym client');
                return;
            }
            if (clientType === 'custom' && !customClient.name) {
                toast.error('Please enter client name');
                return;
            }
            if (clientType === 'custom' && !customClient.phone) {
                toast.error('Please enter client phone number');
                return;
            }
        } else if (step === 2) {
            if (lineItems.some(item => !item.description || !item.quantity || !item.unitPrice)) {
                toast.error('Please fill in all line item details');
                return;
            }
        }
        setStep(step + 1);
    };

    const handleBack = () => {
        setStep(step - 1);
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            const selectedGymData = gyms.find(g => g.id === selectedGym);

            const payload = {
                businessSettingId: selectedBusiness,
                invoiceDate,
                dueDate: dueDate || null,
                clientType,
                clientGymId: clientType === 'gym' ? selectedGym : null,
                clientName: clientType === 'gym' ? selectedGymData?.displayName : customClient.name,
                clientAddress: clientType === 'gym' ? selectedGymData?.address : customClient.address,
                clientPhone: clientType === 'gym' ? selectedGymData?.phoneNumbers[0] : customClient.phone,
                clientGstin: clientType === 'gym' ? (selectedGymData?.includeGstinInInvoices !== false ? selectedGymData?.gstin : '') : customClient.gstin,
                lineItems: lineItems.map(item => ({
                    description: item.description,
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                })),
                discountPercent: discountType === 'percent' ? parseFloat(discountValue) : 0,
                discountAmount: discountType === 'amount' ? parseFloat(discountValue) : 0,
                taxPercent: parseFloat(taxPercent),
                notes,
                termsAndConditions,
                status,
            };

            const url = invoice
                ? `${API_BASE_URL}/api/saas/invoices/invoices/${invoice.id}`
                : `${API_BASE_URL}/api/saas/invoices/invoices`;

            const response = await fetch(url, {
                method: invoice ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to save invoice');
            }

            toast.success(invoice ? 'Invoice updated successfully' : 'Invoice created successfully');
            onSuccess();
        } catch (error: any) {
            console.error('Error saving invoice:', error);
            toast.error(error.message || 'Failed to save invoice');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                            {invoice ? 'Edit Invoice' : 'Create New Invoice'}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Step {step} of 4: {
                                step === 1 ? 'Client & Business Details' :
                                    step === 2 ? 'Line Items' :
                                        step === 3 ? 'Tax & Discount' :
                                            'Review & Generate'
                            }
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                        ×
                    </button>
                </div>

                {/* Progress Bar */}
                <div className="px-6 py-3 bg-gray-50">
                    <div className="flex gap-2">
                        {[
                            { num: 1, label: 'Client & Business' },
                            { num: 2, label: 'Line Items' },
                            { num: 3, label: 'Tax & Discount' },
                            { num: 4, label: 'Review & Generate' }
                        ].map((s) => (
                            <button
                                key={s.num}
                                onClick={() => setStep(s.num)}
                                className={`flex-1 flex flex-col items-center gap-1 py-2 rounded transition-colors ${s.num === step
                                    ? 'bg-indigo-600 text-white'
                                    : s.num < step
                                        ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                                        : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${s.num === step
                                        ? 'bg-white text-indigo-600'
                                        : s.num < step
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-gray-400 text-white'
                                        }`}>
                                        {s.num}
                                    </span>
                                </div>
                                <span className="text-xs font-medium text-center">{s.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Step 1: Client & Business Details */}
                    {step === 1 && (
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Business *
                                </label>
                                {businessSettings.length === 0 ? (
                                    <div className="text-sm text-red-600">
                                        No business settings found. Please create one in Invoice Settings first.
                                    </div>
                                ) : (
                                    <select
                                        value={selectedBusiness}
                                        onChange={(e) => setSelectedBusiness(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        required
                                    >
                                        <option value="">-- Select Business --</option>
                                        {businessSettings.map((bs) => (
                                            <option key={bs.id} value={bs.id}>
                                                {bs.businessName} ({bs.invoicePrefix})
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Client Type *
                                </label>
                                <div className="flex gap-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="gym"
                                            checked={clientType === 'gym'}
                                            onChange={(e) => setClientType(e.target.value as 'gym')}
                                            className="mr-2"
                                        />
                                        Existing Gym Client
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="custom"
                                            checked={clientType === 'custom'}
                                            onChange={(e) => setClientType(e.target.value as 'custom')}
                                            className="mr-2"
                                        />
                                        Custom Client
                                    </label>
                                </div>
                            </div>

                            {clientType === 'gym' ? (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Gym Client *
                                    </label>
                                    <select
                                        value={selectedGym}
                                        onChange={(e) => setSelectedGym(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        required
                                    >
                                        <option value="">-- Select Gym --</option>
                                        {gyms.map((gym) => (
                                            <option key={gym.id} value={gym.id}>
                                                {gym.displayName} - {gym.email}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Client Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={customClient.name}
                                            onChange={(e) => setCustomClient({ ...customClient, name: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Phone Number *
                                        </label>
                                        <input
                                            type="tel"
                                            value={customClient.phone}
                                            onChange={(e) => setCustomClient({ ...customClient, phone: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Address (Optional)
                                        </label>
                                        <textarea
                                            value={customClient.address}
                                            onChange={(e) => setCustomClient({ ...customClient, address: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            rows={3}
                                            placeholder="Enter up to 3 lines of address"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            GSTIN (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={customClient.gstin}
                                            onChange={(e) => setCustomClient({ ...customClient, gstin: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="22AAAAA0000A1Z5"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Invoice Date *
                                    </label>
                                    <input
                                        type="date"
                                        value={invoiceDate}
                                        onChange={(e) => setInvoiceDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Due Date (Optional)
                                    </label>
                                    <input
                                        type="date"
                                        value={dueDate}
                                        onChange={(e) => setDueDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Line Items */}
                    {step === 2 && (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-semibold">Line Items</h3>
                                <button
                                    onClick={addLineItem}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
                                >
                                    + Add Item
                                </button>
                            </div>

                            {lineItems.map((item, index) => (
                                <div key={index} className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex justify-between items-start mb-3">
                                        <h4 className="font-medium text-gray-900">Item {index + 1}</h4>
                                        {lineItems.length > 1 && (
                                            <button
                                                onClick={() => removeLineItem(index)}
                                                className="text-red-600 hover:text-red-800 text-sm"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Description *
                                            </label>
                                            <input
                                                type="text"
                                                value={item.description}
                                                onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                placeholder="Item description"
                                                required
                                            />
                                        </div>
                                        <div className="grid grid-cols-3 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Quantity *
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={item.quantity}
                                                    onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Unit Price (₹) *
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={item.unitPrice}
                                                    onChange={(e) => updateLineItem(index, 'unitPrice', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Amount (₹)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={(parseFloat(item.quantity || '0') * parseFloat(item.unitPrice || '0')).toFixed(2)}
                                                    disabled
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex justify-between text-lg font-semibold">
                                    <span>Subtotal:</span>
                                    <span>₹{calculateSubtotal().toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Tax & Discount */}
                    {step === 3 && (
                        <div className="space-y-6">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Line Items Summary</h3>
                                {lineItems.map((item, index) => (
                                    <div key={index} className="flex justify-between text-sm py-1">
                                        <span>{item.description}</span>
                                        <span>
                                            {item.quantity} × ₹{parseFloat(item.unitPrice).toFixed(2)} = ₹
                                            {(parseFloat(item.quantity) * parseFloat(item.unitPrice)).toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                                <div className="border-t border-gray-300 mt-2 pt-2 flex justify-between font-semibold">
                                    <span>Subtotal:</span>
                                    <span>₹{calculateSubtotal().toFixed(2)}</span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Discount
                                </label>
                                <div className="flex gap-3">
                                    <select
                                        value={discountType}
                                        onChange={(e) => setDiscountType(e.target.value as 'percent' | 'amount')}
                                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="percent">Percentage (%)</option>
                                        <option value="amount">Amount (₹)</option>
                                    </select>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={discountValue}
                                        onChange={(e) => setDiscountValue(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="0"
                                    />
                                    <div className="px-4 py-2 bg-gray-100 rounded-md flex items-center min-w-[120px] justify-end">
                                        ₹{calculateDiscount().toFixed(2)}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Tax (GST) %
                                </label>
                                <div className="flex gap-3">
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        value={taxPercent}
                                        onChange={(e) => setTaxPercent(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <div className="px-4 py-2 bg-gray-100 rounded-md flex items-center min-w-[120px] justify-end">
                                        ₹{calculateTax().toFixed(2)}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-indigo-50 p-4 rounded-lg">
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span>Subtotal:</span>
                                        <span>₹{calculateSubtotal().toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-red-600">
                                        <span>Discount:</span>
                                        <span>- ₹{calculateDiscount().toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Tax ({taxPercent}%):</span>
                                        <span>₹{calculateTax().toFixed(2)}</span>
                                    </div>
                                    <div className="border-t border-indigo-200 pt-2 flex justify-between text-lg font-bold">
                                        <span>Total Amount:</span>
                                        <span>₹{calculateTotal().toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Review & Generate */}
                    {step === 4 && (
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Invoice Status *
                                </label>
                                <select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="PO">Purchase Order / Quotation</option>
                                    <option value="issued">Issued</option>
                                    <option value="paid">Paid</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    {status === 'PO' && 'Will be generated as a Purchase Order/Quotation'}
                                    {status === 'issued' && 'Invoice is issued but not yet paid'}
                                    {status === 'paid' && 'Invoice has been paid'}
                                    {status === 'cancelled' && 'Invoice is cancelled'}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Notes (Optional)
                                </label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    rows={3}
                                    placeholder="Any additional notes for this invoice"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Terms & Conditions (Optional)
                                </label>
                                <textarea
                                    value={termsAndConditions}
                                    onChange={(e) => setTermsAndConditions(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    rows={4}
                                    placeholder="Payment terms, delivery terms, etc."
                                />
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-3">Summary</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Business:</span>
                                        <span className="font-medium">
                                            {businessSettings.find(b => b.id === selectedBusiness)?.businessName}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Client:</span>
                                        <span className="font-medium">
                                            {clientType === 'gym'
                                                ? gyms.find(g => g.id === selectedGym)?.displayName
                                                : customClient.name}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Line Items:</span>
                                        <span className="font-medium">{lineItems.length}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Status:</span>
                                        <span className="font-medium">{status.toUpperCase()}</span>
                                    </div>
                                    <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between text-lg font-bold">
                                        <span>Total Amount:</span>
                                        <span>₹{calculateTotal().toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
                    <button
                        onClick={step === 1 ? onClose : handleBack}
                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                        disabled={loading}
                    >
                        {step === 1 ? 'Cancel' : 'Back'}
                    </button>
                    <button
                        onClick={step === 4 ? handleSubmit : handleNext}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
                        disabled={loading || (step === 1 && businessSettings.length === 0)}
                    >
                        {loading ? 'Processing...' : step === 4 ? (invoice ? 'Update Invoice' : 'Generate Invoice') : 'Next'}
                    </button>
                </div>
            </div>
        </div>
    );
}
